import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Lock, Star, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";

const STATUS_LABELS = {
  new: "New",
  contacted: "Contacted",
  site_visit: "Site visit",
  quoted: "Quote sent",
  completed: "Completed",
};

const STATUS_STYLES = {
  new: "bg-red-50 text-brand-red border-red-200",
  contacted: "bg-violet-50 text-violet-700 border-violet-200",
  site_visit: "bg-amber-50 text-amber-700 border-amber-200",
  quoted: "bg-cyan-50 text-cyan-700 border-cyan-200",
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const inputCls =
  "w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-red focus:ring-4 focus:ring-brand-red/10";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success(`Welcome back, ${data.name}`);
      onLogin(data);
    } catch (err) {
      toast.error("Login failed", { description: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-[80vh] items-center justify-center bg-slate-50 px-5 pt-28" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_24px_70px_rgba(16,24,40,0.12)]" data-testid="admin-login-form">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <LogoMark className="h-12" />
          <h1 className="font-display text-xl font-extrabold text-slate-900">Fusion Glass - Admin</h1>
          <p className="flex items-center gap-2 text-xs text-slate-500">
            <Lock className="h-3 w-3" /> Owner access only
          </p>
        </div>
        <div className="space-y-3">
          <input
            type="email"
            required
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputCls}
            data-testid="admin-login-email"
          />
          <input
            type="password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputCls}
            data-testid="admin-login-password"
          />
          <button
            type="submit"
            disabled={busy}
            className="btn-red w-full disabled:opacity-60"
            data-testid="admin-login-btn"
          >
            {busy ? "Signing in..." : "Sign in"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ customer_name: "", suburb: "", rating: 5, text: "" });

  const load = async () => {
    try {
      const [q, r] = await Promise.all([api.get("/quotes"), api.get("/reviews")]);
      setQuotes(q.data);
      setReviews(r.data);
    } catch (err) {
      toast.error("Could not load data", { description: formatApiError(err) });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}`, { status });
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked "${STATUS_LABELS[status]}"`);
    } catch (err) {
      toast.error("Update failed", { description: formatApiError(err) });
    }
  };

  const addReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { ...reviewForm, rating: Number(reviewForm.rating) });
      setReviewForm({ customer_name: "", suburb: "", rating: 5, text: "" });
      toast.success("Review published to the website");
      load();
    } catch (err) {
      toast.error("Could not add review", { description: formatApiError(err) });
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((rs) => rs.filter((r) => r.id !== id));
      toast.success("Review removed");
    } catch (err) {
      toast.error("Delete failed", { description: formatApiError(err) });
    }
  };

  const newCount = quotes.filter((q) => q.status === "new").length;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container-x pb-24 pt-12">
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="eyebrow mb-2 block">Admin dashboard</p>
            <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Quote requests &amp; reviews</h1>
          </div>
          <button
            onClick={async () => {
              await api.post("/auth/logout").catch(() => {});
              onLogout();
            }}
            className="flex items-center gap-2 rounded-full border-2 border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-brand-red hover:text-brand-red"
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-stats-total">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Total quotes</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900">{quotes.length}</p>
          </div>
          <div className="rounded-2xl border-2 border-brand-red/25 bg-brand-red/[0.04] p-5" data-testid="admin-stats-new">
            <p className="font-mono text-xs uppercase tracking-widest text-brand-red">New, needs a call</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-brand-red">{newCount}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-stats-reviews">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-500">Published reviews</p>
            <p className="mt-2 font-display text-3xl font-extrabold text-slate-900">{reviews.length}</p>
          </div>
        </div>

        <div className="mb-14 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="admin-quotes-table">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left text-sm">
              <thead className="bg-slate-50 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Received</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Service</th>
                  <th className="px-4 py-3">Suburb</th>
                  <th className="px-4 py-3">Details</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {quotes.length === 0 && (
                  <tr>
                    <td colSpan="7" className="px-4 py-10 text-center text-slate-500">
                      No quote requests yet - they'll appear here the moment someone submits the form.
                    </td>
                  </tr>
                )}
                {quotes.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50" data-testid="admin-quote-row">
                    <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500">
                      {new Date(q.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                      <br />
                      {new Date(q.created_at).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-4 font-semibold text-slate-900">
                      {q.name}
                      {q.status === "new" && <span className="ml-2 inline-block h-2 w-2 rounded-full bg-brand-red" />}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 text-xs">
                      <a href={`tel:${q.phone}`} className="block font-semibold text-brand-red hover:underline">{q.phone}</a>
                      <span className="text-slate-500">{q.email}</span>
                    </td>
                    <td className="px-4 py-4 text-slate-700">{q.service}</td>
                    <td className="px-4 py-4 text-slate-700">{q.suburb}</td>
                    <td className="max-w-[240px] px-4 py-4">
                      <p className="truncate text-xs text-slate-600" title={q.message}>{q.message || "-"}</p>
                      {q.how_heard && <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-slate-400">via {q.how_heard}</p>}
                    </td>
                    <td className="px-4 py-4">
                      <select
                        value={q.status}
                        onChange={(e) => setStatus(q.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-semibold outline-none ${STATUS_STYLES[q.status]}`}
                        data-testid={`admin-quote-status-${q.id}`}
                      >
                        {Object.entries(STATUS_LABELS).map(([v, l]) => (
                          <option key={v} value={v} className="bg-white text-slate-800">{l}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <h2 className="mb-5 font-display text-xl font-extrabold text-slate-900">Add a customer review</h2>
            <form onSubmit={addReview} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-6" data-testid="admin-review-form">
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  required
                  minLength={2}
                  placeholder="Customer name"
                  value={reviewForm.customer_name}
                  onChange={(e) => setReviewForm((f) => ({ ...f, customer_name: e.target.value }))}
                  className={inputCls}
                  data-testid="admin-review-name"
                />
                <input
                  placeholder="Suburb (e.g. Bondi)"
                  value={reviewForm.suburb}
                  onChange={(e) => setReviewForm((f) => ({ ...f, suburb: e.target.value }))}
                  className={inputCls}
                  data-testid="admin-review-suburb"
                />
              </div>
              <select
                value={reviewForm.rating}
                onChange={(e) => setReviewForm((f) => ({ ...f, rating: e.target.value }))}
                className={inputCls}
                data-testid="admin-review-rating"
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n} className="bg-white">{n} star{n > 1 ? "s" : ""}</option>
                ))}
              </select>
              <textarea
                required
                minLength={10}
                rows={3}
                placeholder="Their review..."
                value={reviewForm.text}
                onChange={(e) => setReviewForm((f) => ({ ...f, text: e.target.value }))}
                className={inputCls}
                data-testid="admin-review-text"
              />
              <button
                type="submit"
                className="btn-red"
                data-testid="admin-review-submit-btn"
              >
                <Star className="h-4 w-4" /> Publish review
              </button>
            </form>
          </div>

          <div>
            <h2 className="mb-5 font-display text-xl font-extrabold text-slate-900">Published reviews</h2>
            <div className="space-y-3">
              {reviews.length === 0 && (
                <p className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
                  No reviews yet. Add your first customer review and it will appear on the homepage.
                </p>
              )}
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="admin-review-item">
                  <div>
                    <p className="flex items-center gap-1.5 font-display text-sm font-extrabold text-slate-900">
                      {r.customer_name} <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{r.suburb}</span>
                    </p>
                    <div className="my-1.5 flex gap-0.5">
                      {Array.from({ length: r.rating }).map((_, i) => (
                        <Star key={i} className="h-3 w-3 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-xs leading-relaxed text-slate-600">{r.text}</p>
                  </div>
                  <button
                    onClick={() => deleteReview(r.id)}
                    className="rounded-lg border border-slate-200 p-2 text-slate-400 transition-colors hover:border-brand-red hover:text-brand-red"
                    aria-label={`Delete review by ${r.customer_name}`}
                    data-testid={`admin-review-delete-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(null));
  }, []);

  if (user === undefined) {
    return <div className="min-h-[80vh] bg-slate-50" />;
  }
  if (!user) return <Login onLogin={setUser} />;
  return <Dashboard user={user} onLogout={() => setUser(null)} />;
}
